
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * View Transitions API Polyfill
   *
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    HTMLUpdateUtility.runPreProcessCallbacks(newContent, preProcessCallbacks)

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // // dedupe IDs
    // const uniqueKey = Date.now();
    // oldNode.querySelectorAll('[id], [form]').forEach((element) => {
    //   element.id && (element.id = `${element.id}-${uniqueKey}`);
    //   element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    // });

    oldNode.parentNode.replaceChild(newNode, oldNode);
    //oldNode.setAttribute('remove-node','');
    //oldNode.style.display = 'none';

    HTMLUpdateUtility.runPostProcessCallbacks(newNode, postProcessCallbacks);

    // this half second delay could cause issues when the javascripts get triggered on the new dom
    // with both the old and new dom in the dom tree
    // it is only needed if we do some animations, but if we really do that,
    // all javascripts w/o runPostProcessCallbacks should be excluded from section rendering refresh and
    // the runPostProcessCallbacks together with third party javascript reinitialize events
    // should be triggered after dom remove
    //setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  // should be called before view transition instead
  // Run 1st (Pre) buffer approach.
  static runPreProcessCallbacks(newContent, preProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));
  }

  // Run 2nd (Post) buffer approach.
  static runPostProcessCallbacks(newNode, postProcessCallbacks = []) {
    postProcessCallbacks?.forEach((callback) => callback(newNode));
  }
}

/**
  * COOKIE MONSTER -- https://www.quirksmode.org/js/cookies.html
  */
/*const Cookies = {
  set: function (cname, cvalue, options) {
    options = options || {};
    var expires = "";
    var cookieDomain = "";
    if ('ttl' in options) {
      var now = new Date();
      now.setTime(now.getTime() + (options.ttl * 1000 * 60));
      expires = ';expires=' + now.toUTCString();
    } else if ('months' in options) {
      var now = new Date();
      now.setMonth(now.getMonth() + options.months)
      expires = ';expires=' + now.toUTCString();
    } else if ('midnight' in options) {
      var now = new Date();
      var date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      expires = ';expires=' + date.toUTCString();
    }
    if ('domain' in options) {
      cookieDomain = ';domain=' + domain;
    }
    // document.cookie = cname + "=" + cvalue + expires + cookieDomain + ";path=/";
    document.cookie = cname + '=' + escape(cvalue) + expires + cookieDomain + ';path=/';
  },
  get: function (cname) {
    var name = cname + '=';
    var decodedCookie = decodeURIComponent(document.cookie);
    var ca = decodedCookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        // return c.substring(name.length, c.length);
        return unescape(c.substring(name.length, c.length));
      }
    }
    return null;
  },
  reset: function (cname) { document.cookie = cname + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'; },
};
*/

/**
 * Sets Accessibility stuff for disclosure widgets and select dropdowns (custom).
 */
function setupDetailsSummary() {
  document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

    if (summary.nextElementSibling.getAttribute('id')) {
      summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    }

    summary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (summary.closest('[data-header-drawer], [data-menu-drawer]')) return;
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  });
}
setupDetailsSummary();

/**
 * Sets Accessibility stuff for select dropdowns (custom).
 * - Inspiration: https://getbootstrap.com/docs/5.3/components/dropdowns/#accessibility
 */
function setupSelectDropdowns() {
  document.querySelectorAll('.select-dropdown[data-select-dropdown] button.select-dropdown__button').forEach((button) => {
    button.setAttribute('role', 'button');
    button.setAttribute('aria-expanded', button.parentNode.hasAttribute('open'));

    if (button.nextElementSibling.getAttribute('id')) {
      button.setAttribute('aria-controls', button.nextElementSibling.id);
    }

    button.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('[data-select-dropdown]').hasAttribute('open'));
    });

    if (button.closest('[data-embroidery-modal], [data-product-modal], [data-modal-dialog]')) return;
    button.parentElement.addEventListener('keyup', onKeyUpEscape);
  });
}
setupSelectDropdowns();

/**
 * Sets Accessibility stuff for tabs widgets.
 * Inspiration: https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */
function setupTablistTabs() {
  document.querySelectorAll('[role="tablist"] [role="tab"]').forEach((tab) => {
    const tabpanel = document.getElementById(tab.getAttribute('aria-controls'));

    tab.setAttribute('type', 'button');
    tab.setAttribute('aria-selected', tabpanel.hasAttribute('open'));
    tab.setAttribute('tabindex', tabpanel.hasAttribute('open') ? 0 : -1);

    tab.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-selected', tabpanel.hasAttribute('open'));
    })

    tabpanel.setAttribute('role', 'tabpanel');
    tabpanel.setAttribute('tabindex', tabpanel.hasAttribute('open') ? 0 : -1);
    tabpanel.setAttribute('aria-labelledby', tab.getAttribute('id'));
  });
}
setupTablistTabs();


/**
 * Sets Accessibility stuff for Simple Modals.
 * Inspiration: https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */
function hideSimpleModal(event) {
  const containerElement = event.target.closest('.simple-modal');
  if (!containerElement) return;

  // i.e. href="#product-size-guide-modal"
  const openedBy = document.querySelector('[href="#' + containerElement.getAttribute('id') + '"]');
  if (openedBy) {
    if (openedBy.getAttribute('data-trap-focus') != 'false') {
      window.removeTrapFocus(openedBy);
    } else {
      window.removeTrapFocus();
    }
  }
}

function onKeyupSimpleModal(event) {
  if (event.code?.toUpperCase() === 'ESCAPE') {
    const containerElement = event.target.closest('.simple-modal');
    const modalClose = containerElement.querySelector('[id^="ModalClose-"]');
    modalClose?.click();
    // window.ScrollLock.unlock();
  }
}

function setupSimpleModals() {
  document.querySelectorAll('.simple-modal a[id^="ModalClose-"]').forEach((button) => {
    if (button.tagName !== 'A') return;
    const containerElement = button.closest('.simple-modal');
    const cancelButton = containerElement.querySelector('.cancel');
    containerElement?.addEventListener(
      'transitionend',
      () => {
        window.trapFocus(containerElement, button);
        // console.log('trapFocus', containerElement);
      }
    );
    containerElement?.addEventListener('keyup', onKeyupSimpleModal);
    button?.addEventListener('click', hideSimpleModal);
    cancelButton?.addEventListener('click', hideSimpleModal);
    // console.log(containerElement);
  });
}
setupSimpleModals();

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if(!elementToFocus) return;

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (openDetailsElement) {
    const summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
  }

  const openSelectElement = event.target.closest('.select-dropdown[open]');
  if (openSelectElement) {
    const buttonElement = openDetailsElement.querySelector('button.select-dropdown__button');
    openSelectElement.removeAttribute('open');
    buttonElement.setAttribute('aria-expanded', false);
    buttonElement.focus();
  }
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function waitForElement(node, selector) {
  return new Promise((resolve, reject) => {
    const el = document.querySelector(selector);
    if (el) {
      resolve(el);
      return;
    }
    const o = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        o.disconnect();
        resolve(el);
        return;
      }
    });
    o.observe(node ?? document.documentElement, { childList: !0 });
  });
}

/**
 * merge two or more objects vanilla js
 *
 * All credits to author.
 * https://gomakethings.com/vanilla-javascript-version-of-jquery-extend/
 *
 * @param   {Boolean}  deep     If true, do a deep (or recursive) merge [optional]
 * @param   {Object}   objects  The objects to merge together
 * @returns {Object}            Merged values of defaults and options
 */
function extend() {
  var extended = {};
  var deep = false;
  var i = 0;
  // Check if a deep merge
  if (typeof (arguments[0]) === 'boolean') {
    deep = arguments[0];
    i++;
  }
  // Merge the object into the extended object
  var merge = function (obj) {
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        if (deep && Object.prototype.toString.call(obj[prop]) === '[object Object]') {
          // If we're doing a deep merge and the property is an object
          extended[prop] = extend(true, extended[prop], obj[prop]);
        } else {
          // Otherwise, do a regular merge
          extended[prop] = obj[prop];
        }
      }
    }
  };

  // Loop through each object and conduct a merge
  for (; i < arguments.length; i++) { merge(arguments[i]); }
  return extended;
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function trackEmailSignup(pointOfSignup = '') {
  if (window.heap) {
    window.heap.track('email_signup', { point_of_signup: pointOfSignup });
  }
}

async function applyCartDiscount(discountCode) {
  if(!discountCode || discountCode === 'null' || discountCode === 'undefined' || discountCode === '') {
    console.log('discountCode is empty, skipping');
    return
  }
  const discountCodeLower = discountCode.toLowerCase();
  // if(!checkShouldAutoApply(discountCodeLower)) {
  //   console.log('should not auto apply, returning');
  //   return;
  // }
  await applyCartDiscountNoStoreFront(discountCodeLower);
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';'); // Split the string into individual cookie pairs
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') { // Remove leading whitespace
      c = c.substring(1, c.length);
    }
    if (c.indexOf(nameEQ) === 0) { // Check if the cookie starts with the desired name
      return decodeURIComponent(c.substring(nameEQ.length, c.length)); // Return the decoded value
    }
  }
  return null; // Return null if the cookie is not found
}

async function applyCartDiscountNoStoreFront(discountCode = '') {
  if(!discountCode || discountCode === 'null' || discountCode === 'undefined' || discountCode === '') {
    console.log('couponCode is empty, skipping');
    return false;
  }

  console.log('applyCartDiscountNoStoreFront', discountCode);


  // update: trigger auto apply logic only if partner is logged in for now
  // to minimize race conditions while looking into the bold metrics issue
  let sitePartnerHandle = Shopify.jaanuu.partner_handle;
  //normalize
  if (typeof sitePartnerHandle === 'string') {
    sitePartnerHandle = sitePartnerHandle.trim();
    if (sitePartnerHandle.length === 0 ) {
      sitePartnerHandle = null;
    }
  }
  if (!sitePartnerHandle) {
    console.log("not auto applying coupon with api call for now when partner not logged in until we have a use case for it");
    return false;
  }

  const cartLimited = JSON.parse(document.getElementById('initialShoppingCartState').innerText);
  if(!cartLimited) {
    console.error('Cart not found, maybe forgot to load cart-automation.liquid');
    return false;
  }

  // workaround
  let hasPhysicalProduct = false;
  for (const item of cartLimited.items) {
    if(item.requires_shipping && !item.title.includes('Embroidery')) {
      hasPhysicalProduct = true;
    }
    //console.log('item',item);
    if(item.discounts.some(discount => discount.title.toLowerCase() === discountCode.toLowerCase())) {
      console.log('Discount already applied from cartLimited,skipping');
      return false;
    }
  }

  //workaround to avoid race condition where there are embroidery items or non-physical items in the cart pending removal
  if(!hasPhysicalProduct && cartLimited.items.length > 0) {
    console.log('No physical product in cart and cart not empty, skipping discount application');
    return;
  }


  // shopify's liquid is more limited than its ajax, which doesn't expose coupons on the cart that are not applied successfully
  // get the most current cart from cart ajax call instead
  const cartWithCouponResponse = await fetch(window.Shopify.routes.root + 'cart.js');
  const cartWithCoupon = await cartWithCouponResponse.json();

  console.log('applyCartDiscount',cartWithCoupon);

  if (cartWithCoupon.discount_codes.some(discount => discount.code.toLowerCase() === discountCode.toLowerCase())) {
    console.log('Discount already applied,skipping');
    return;
  }


  // //prevent the function from running multiple times
  // if(getCookieByName('discount_code') === discountCode) {
  //   console.log('Discount already applied through cookie,skipping');
  //   return;
  // }
  // // Save the discount code as a session cookie
  // document.cookie = `discount_code=${discountCode}; path=/`;

  // apply discount code
  // delete cookied shopify discount code first, otherwise will reset coupon at checkout
  document.cookie = "discount_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  const response = await fetch(Shopify.routes.cart_update_url, {
    method: 'POST',
    headers: {
      "accept": "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ discount: discountCode })
  });

  const data = await response.json();
  console.log('after applyCartDiscount',data);
}

async function updateCartEmbroidery(cart) {
  const sections = ['cart-drawer','cart-icon-bubble'];

  const embroideryFeeVariantId = Shopify.jaanuu.embroidery_fee_variant_id;
  if (!embroideryFeeVariantId) {
    console.error('Embroidery fee variant not found');
    return;
  }

  const update_url = Shopify.routes.cart_update_url;
  const add_url = Shopify.routes.cart_add_url;

  // const cart = JSON.parse(document.getElementById('shoppingCartState').innerText);
  // console.log('shoppingCartState',cart);

  let hasEmbroidery = false;

  const extra_remove_keys = [];
  for (const item of cart.items) {
    let prop = item?.properties?._embroidery?.toString()?.trim();
    if (prop && prop !== '' && prop !== '{}' && prop !== 'null' && prop !== 'undefined') {
      hasEmbroidery = true;
    }
  }

  if (!hasEmbroidery) {
    console.log('No embroidery');
    // remove all embroidery fee items
    for (const item of cart.items) {
      if (item.variant_id === embroideryFeeVariantId) {
        extra_remove_keys.push(item.key);
      }
    }

    if (extra_remove_keys.length > 0) {
      const updates = {
        updates: extra_remove_keys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {})
      };

      updates['sections'] = sections;

      const body = JSON.stringify(updates);
      const response = await fetch(update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body
      });

      const data = await response.json();
      console.log(data);

      window.publish(PUB_SUB_EVENTS.cartUpdate, {
        source: '[automation]',
        productVariantId: embroideryFeeVariantId,
        cartData: data,
      });

      // const cartDrawer = document.querySelector('[data-cart-drawer]');
      // if (cartDrawer) {
      //   const cartDrawerComponent = Shopify.getComponent(cartDrawer);
      //   cartDrawerComponent?.renderContents(data);
      // }
      return true;
    }
  } else { //has embroidery
    console.log('Embroidery found');
    let existingEmbroideryFeeKey = null;
    // remove extra embroidery fee items
    for (const item of cart.items) {
      if (item.variant_id === embroideryFeeVariantId) {
        if (existingEmbroideryFeeKey) {
          extra_remove_keys.push(item.key);
        } else {
          existingEmbroideryFeeKey = item.key;
        }
      }
    }
    let hash = {
      updates: extra_remove_keys.reduce((acc, key) => {
        acc[key] = 0;
        return acc;
      }, {})
    };
    if (existingEmbroideryFeeKey) { // just clean up, don't trigger cart refresh event

      //hash['sections'] = sections;

      if(hash && hash.updates && Object.keys(hash.updates).length === 0) {
        console.log('existingEmbroideryFeeKey No updates needed');
        return false;
      }

      const body = JSON.stringify(hash);

      const response = await fetch(update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body
      });
      const data = await response.json();
      console.log(data);

      // window.publish(PUB_SUB_EVENTS.cartUpdate, {
      //   source: '[automation]',
      //   productVariantId: embroideryFeeVariantId,
      //   cartData: data,
      // });
    } else { // add missing embroidery fee item
      const updates = {
        items: [
          {
            id: embroideryFeeVariantId,
            quantity: 1,
            properties: {
              ['Final Sale']: "No Returns or exchanges"
            }
          }
        ],
        sections: sections
      };

      const body = JSON.stringify(updates);
      const response = await fetch(add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body
      });

      const data = await response.json();
      console.log(data);

      // const cartDrawer = document.querySelector('[data-cart-drawer]');
      // if (cartDrawer) {
      //   const cartDrawerComponent = Shopify.getComponent(cartDrawer);
      //   cartDrawerComponent?.renderContents(data);
      // }

      window.publish(PUB_SUB_EVENTS.cartUpdate, {
        source: '[automation]',
        productVariantId: embroideryFeeVariantId,
        fromAddToCart: true,
        cartData: data,
        hasCartDrawerSections: true, // has sections[cart-drawer] and sections[cart-icon-bubble] under cartDrawerSections that can be used w/o the need for another ajax call
      });
      return true;
    }
  }
  return false;
}

// curPartnerEmployeeValue should be the current partner employee value from the session that is not stored
async function updateCartPartnerStatus(cart,curPartnerEmployeeValue) {
  //return false;

  if (!curPartnerEmployeeValue || curPartnerEmployeeValue === 'null' || curPartnerEmployeeValue === 'undefined' || curPartnerEmployeeValue === '') {
    curPartnerEmployeeValue = "no_partner";
  }

  const finalSessionPartnerEmployeeValue = curPartnerEmployeeValue;

  let storedPartnerEmployeeValue = localStorage.getItem("jaPartnerEmployeeValueV2");

  // some partners might not have stored partner employee value
  // if storedPartnerEmployeeValue is empty, then we assume it is the same as finalSessionPartnerEmployeeValue
  // otherwise would log them out by accident
  if (!storedPartnerEmployeeValue || storedPartnerEmployeeValue === 'null' || storedPartnerEmployeeValue === 'undefined' || storedPartnerEmployeeValue === '') {
    storedPartnerEmployeeValue = finalSessionPartnerEmployeeValue;
  }

  if (!storedPartnerEmployeeValue || storedPartnerEmployeeValue === 'null' || storedPartnerEmployeeValue === 'undefined' || storedPartnerEmployeeValue === '') {
    storedPartnerEmployeeValue = "no_partner";
  }

  let didClearData = false;
  //let partner_handle_matches = true;
  let cartPartnerHandle = cart.attributes?.partner_handle;

  //normalize
  if (typeof cartPartnerHandle === 'string') {
    cartPartnerHandle = cartPartnerHandle.trim();
    if (cartPartnerHandle.length ===0 ) {
      cartPartnerHandle = null;
    }
  }

  let sitePartnerHandle = Shopify.jaanuu.partner_handle;

  //normalize
  if (typeof sitePartnerHandle === 'string') {
    sitePartnerHandle = sitePartnerHandle.trim();
    if (sitePartnerHandle.length === 0 ) {
      sitePartnerHandle = null;
    }
  }

  // do clear when switching between d2c and partner employee
  let doClear = false;

  // partner not logged in, clear only if cart has partner handle
  // to avoid clearing regular carts by accident
  if(finalSessionPartnerEmployeeValue == "no_partner") {
    // need to check cartPartnerHandle in case the user doesn't have storedPartnerEmployeeValue
    // consider adding || finalSessionPartnerEmployeeValue !== storedPartnerEmployeeValue
    // if you really want to be extra safe, but adding it will cause cart to be cleared
    // one extra time if the session has been inactive for long time where the cart token is
    // a new one anyways
    if (cartPartnerHandle || finalSessionPartnerEmployeeValue !== storedPartnerEmployeeValue) {
      let payload = {
        "sitePartnerHandle": sitePartnerHandle,
        "cartPartnerHandle": cartPartnerHandle,
        "finalSessionPartnerEmployeeValue": finalSessionPartnerEmployeeValue,
        "storedPartnerEmployeeValue": storedPartnerEmployeeValue,
      };
      // added this for monitoring to see if there are spikes
      window.JaFrontendNotifyError("Partner logged out, clearing cart", 'info', payload);
      //alert("Partner logged out, clearing cart");
      doClear = true;
    }
  } else { // partner logged in

    // update partner embroidery fee variables
    let partnerEmbroideryPricing = null;

    const partnerEmbroideryPriceJson = document.getElementById('partnerEmbroideryPricing')?.innerText;
    if (partnerEmbroideryPriceJson && partnerEmbroideryPriceJson.trim().length > 0) {
      try {
        partnerEmbroideryPricing = JSON.parse(partnerEmbroideryPriceJson);
      } catch (e) {
        console.error('Error parsing partnerEmbroideryPricing JSON', e);
      }
      console.log('partnerEmbroideryPricing', partnerEmbroideryPricing);
    }

    if (partnerEmbroideryPricing) {
      const cartToken = getCookie('cart');

      let sessionStorageEmbroideryAttributes = null;
      let sessionStorageEmbroideryKey = null
      let keySuffix = '';
      if (cartToken && cartToken.length > 8) {
        keySuffix = cartToken.split('?')[0];
        sessionStorageEmbroideryKey = `jaCartEmbroideryPriceAttributes_${keySuffix}`;

        const sessionStorageEmbroideriesText = window.sessionStorage.getItem(sessionStorageEmbroideryKey);
        if (sessionStorageEmbroideriesText) {
          try {
            sessionStorageEmbroideryAttributes = JSON.parse(sessionStorageEmbroideriesText);
          } catch (e) {
            console.error('Error parsing sessionStorageEmbroideryAttributes JSON', e);
          }
        }
      }


      const cartEmbroideryPriceAttributes = {
        "__custom_text_embroidery_price": partnerEmbroideryPricing?.partner_embroidery_pricing || null, // for text
        "__custom_per_logo_embroidery_price": partnerEmbroideryPricing?.partner_embroidery_pricing_per_logo || null,
      };

      let doUpdateEmbroideryAttributes = true;
      if (sessionStorageEmbroideryAttributes) {
        // compare to avoid unnecessary cart update calls
        if (sessionStorageEmbroideryAttributes.__custom_text_embroidery_price === cartEmbroideryPriceAttributes.__custom_text_embroidery_price &&
          sessionStorageEmbroideryAttributes.__custom_per_logo_embroidery_price === cartEmbroideryPriceAttributes.__custom_per_logo_embroidery_price) {
          console.log('Embroidery price attributes unchanged, skipping cart update');
          doUpdateEmbroideryAttributes = false;
        }
      }

      if (doUpdateEmbroideryAttributes) {
        console.log('Updating embroidery price attributes on cart', cartEmbroideryPriceAttributes);
        // update partner embroidery fee, need to run all the time with partner with embroidery on the
        // cart because there are no other way to avoid this except to make those attributes below non-private
        const response = await fetch(Shopify.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            attributes: cartEmbroideryPriceAttributes
          })
        });
        const data = await response.json();

        if (response.ok) {
          if (sessionStorageEmbroideryKey) {
            window.sessionStorage.setItem(sessionStorageEmbroideryKey, JSON.stringify(cartEmbroideryPriceAttributes));
          }
        }
      }

    }

    // partner logged in
    if (finalSessionPartnerEmployeeValue !== storedPartnerEmployeeValue) {
      // pretty rare
      let payload = {
        "sitePartnerHandle": sitePartnerHandle,
        "cartPartnerHandle": cartPartnerHandle,
        "finalSessionPartnerEmployeeValue": finalSessionPartnerEmployeeValue,
        "storedPartnerEmployeeValue": storedPartnerEmployeeValue,
      };
      // added this for monitoring to see if there are spikes
      window.JaFrontendNotifyError("partner employee value changed, clearing cart", 'info', payload);
      //alert("partner employee value changed, clearing cart");
      doClear = true;
    }
  }

  if (doClear) {
    // delete Social Snowball/creatorcommerce cookies because they interfere with the cart and auto apply discount
    // cookie names are from https://cdn.shopify.com/extensions/c8cb36b9-7fd8-49c1-ba0d-1bec577f56e3/creatorcommerce-creator-shops-26/assets/ccsdk-production.js
    document.cookie = "cc-creator-shop-id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "cc-creator-shop-handle=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "cc-campaign-id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "cc-creator-shop-discount=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "cc-creator-shop-discount-value=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    const clearResponse = await fetch(Shopify.routes.cart_clear_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
    });
    await clearResponse.json();
    if(getCookie('cart')) {
      //alert("clear cart cookie");
      document.cookie = "cart=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }
    didClearData = true;
  }

  // update to avoid clearing cart again
  localStorage.setItem("jaPartnerEmployeeValueV2", finalSessionPartnerEmployeeValue);

  if (didClearData){
    return didClearData;
  }

  // current partner is not the same as cart partner
  if(cartPartnerHandle != sitePartnerHandle) {

    // update attribute
    const update_url = Shopify.routes.cart_update_url;

    const partnerCompanyName = Shopify.jaanuu.partner?.company_name;

    const body = JSON.stringify({
      attributes: {
        partner_handle: sitePartnerHandle,
        partner_company_name: partnerCompanyName,
      }
    });

    const response = await fetch(update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: body
    });
    const data = await response.json();
    console.log(data);
  }
  return didClearData;
}

//
// Third-party vendor tracking
// Fire code after the addToCart event, check action to see if it was an add or update
// -- Supports: regular products and scrub sets actions
window.subscribe(PUB_SUB_EVENTS.addToCart, function (event = {}) {
  Shopify.debug && console.log('PUB_SUB_EVENTS.addToCart analytics', event);

  if (event.source == '[data-product-form]' && event.data) {
    const variant = event.data;
    // console.log('variant ---', variant);
    let product_name;
    let sku_prefix;
    try {
      product_name = variant.name.split('/')[0];
      sku_prefix = variant.sku.split('-')[0] + '-' + variant.sku.split('-')[1];
    } catch (e) {
      product_name = variant.name;
      sku_prefix = variant.sku;
      console.log("addToCart parsing data generated an error", e);
    }

    if (window.heap) {
      const properties = {
        product_id: variant?.featured_image?.product_id,
        product_name: product_name,
        name: variant.name,
        id: variant.id,
        color: variant.option1,
        size: variant.option2,
        sku_prefix: sku_prefix,
        sku: variant.sku,
      }
      window.heap.track('AddToCart', properties);
    }
  }

  if (event.source == '[ScrubSetLayer]' && event.data && 'product_variants' in event.data) {
    const prodProperties = event.data;

    // Analytics stuff
    prodProperties.product_variants.forEach((variant, index0) => {
      const product = prodProperties.products[index0];

      // Send to GA
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'Added To Cart' });
      }

      if (window._mfq) {
        window._mfq.push(["tag", "Add To Cart"]);
      }

      let product_name;
      try {
        product_name = variant.name.split('/')[0];
      } catch(e) {
        product_name = variant.name;
        console.log("addToCart parsing data generated an error", e);
      }

      if (window.heap) {
        const properties = {
          product_id: product.id,
          product_name: product_name,
          master_product_id: product.master_product.id,
          name: variant.name,
          id: variant.id,
          color: product.color.name,
          size: variant.option2,
          // quantity: product.quantity,
          sku_prefix: product.sku_prefix,
          sku: variant.sku
        }
        window.heap.track('AddToCart', properties);
      }

    });
  }
});

// similar to "onProductSelectionChanged" function (from product_detail_new.jsx)
window.subscribe(PUB_SUB_EVENTS.pdpVariantChanged, function(event = {}) {
  Shopify.debug && console.log('PUB_SUB_EVENTS.pdpVariantChanged', event);

  if (event.source == '[data-variant-selects]' && event.variant) {
    let variant;
    let product_name;
    let sku_prefix;
    try {
      variant = event.variant;
      // console.log('variant ---', variant);
      product_name = variant.name.split('/')[0];
      sku_prefix = variant.sku.split('-')[0] + '-' + variant.sku.split('-')[1];
    } catch(e) {
      product_name = variant.name;
      sku_prefix = "";
      console.log("pdpVariantChanged parsing data generated an error", e);
    }

    if (window.Shopify && window.Shopify.jaanuu.heap) {
      /**
       * Snapshot properies are labeled:
       * sku, sku_prefix, product_name, name, color, size
       */
      Shopify.jaanuu.heap.product_variant = {
        name: variant.name,
        sku: variant.sku,
        sku_prefix: sku_prefix,
        product_name: product_name.trim(),
        product_id: variant?.featured_image?.product_id,
        color_name: variant.option1,
        size_name: variant.option2
      };
    }
  }
});

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};
